import { QueryDataParams } from '@/types/query';
import { PROJECT_ID } from '@/constants/index';

export interface Route {
  route: string;
  url: string;
}
export const ROUTES = {
  HOME: {
    route: '/',
    url: '/index.html',
  } as Route,

  RESET_PASSWORD: {
    route: '/reset-password',
    url: '/reset-password.html',
  } as Route,

  CONFIRM_EMAIL: {
    route: '/confirm-email',
    url: '/confirm-email.html',
  } as Route,

  ADMIN: {
    ROOT: {
      route: '/adminx',
      url: '/adminx/index.html',
    } as Route,

    PROJECTS: {
      route: '/adminx/projects',
      url: '/adminx/projects.html',
    } as Route,
    ADD_PROJECT: {
      route: '/adminx/add-project',
      url: '/adminx/add-project.html',
    } as Route,
    EDIT_PROJECT: {
      route: '/adminx/edit-project',
      url: '/adminx/edit-project.html',
    } as Route,
    PROJECT_USERS: {
      route: '/adminx/project-users',
      url: '/adminx/project-users.html',
    } as Route,
    PROJECT_MAPS: {
      route: '/adminx/project-maps',
      url: '/adminx/project-maps.html',
    } as Route,
    PROJECT_OVERLAYS: {
      route: '/adminx/project-overlays',
      url: '/adminx/project-overlays.html',
    } as Route,
    PROJECT_COMMENTS: {
      route: '/adminx/project-comments',
      url: '/adminx/project-comments.html',
    } as Route,

    USERS: {
      route: '/adminx/users',
      url: '/adminx/users.html',
    } as Route,
    ADD_USER: {
      route: '/adminx/add-user',
      url: '/adminx/add-user.html',
    } as Route,
    EDIT_USER: {
      route: '/adminx/edit-user',
      url: '/adminx/edit-user.html',
    } as Route,
    EDIT_PRIVILEGES: {
      route: '/adminx/edit-privileges',
      url: '/adminx/edit-privileges.html',
    } as Route,

    PLUGINS: {
      route: '/adminx/plugins',
      url: '/adminx/plugins.html',
    } as Route,
    EDIT_PLUGIN: {
      route: '/adminx/edit-plugin',
      url: '/adminx/edit-plugin.html',
    } as Route,

    CONFIG: {
      route: '/adminx/config',
      url: '/adminx/config.html',
    } as Route,
    REGISTER_IN_MINERVA_NET: {
      route: '/adminx/register-in-minerva-net',
      url: '/adminx/register-in-minerva-net.html',
    } as Route,

    PERMALINKS: {
      route: '/adminx/permalinks',
      url: '/adminx/permalinks.html',
    } as Route,
    ADD_PERMALINK: {
      route: '/adminx/add-permalink',
      url: '/adminx/add-permalink.html',
    } as Route,
    EDIT_PERMALINK: {
      route: '/adminx/edit-permalink',
      url: '/adminx/edit-permalink.html',
    } as Route,

    ANNOTATORS: {
      ROOT: {
        route: '/adminx/annotators',
        url: '/adminx/annotators.html',
      } as Route,
      PRESETS: {
        NEW: {
          route: '/adminx/annotators/presets/new',
          url: '/adminx/annotators/presets/new.html',
        } as Route,
        EDIT: {
          route: '/adminx/annotators/presets/edit',
          url: '/adminx/annotators/presets/edit.html',
        } as Route,
      },
    },
  },
} as const;

export const buildConfigRoute = (type: string): Route => {
  return {
    route: `${ROUTES.ADMIN.CONFIG.route}?type=${type.toLowerCase().replace(/\s+/g, '-')}`,
    url: `${ROUTES.ADMIN.CONFIG.url}?type=${type.toLowerCase().replace(/\s+/g, '-')}`,
  };
};

export const buildEditPluginRoute = (hash: string): Route => ({
  route: `${ROUTES.ADMIN.EDIT_PLUGIN.route}?hash=${hash}`,
  url: `${ROUTES.ADMIN.EDIT_PLUGIN.url}?hash=${hash}`,
});

export const buildEditProjectRoute = (projectId: string): Route => ({
  route: `${ROUTES.ADMIN.EDIT_PROJECT.route}?id=${projectId}`,
  url: `${ROUTES.ADMIN.EDIT_PROJECT.url}?id=${projectId}`,
});

export const buildProjectMapsRoute = (projectId: string): Route => ({
  route: `${ROUTES.ADMIN.PROJECT_MAPS.route}?id=${projectId}`,
  url: `${ROUTES.ADMIN.PROJECT_MAPS.url}?id=${projectId}`,
});

export const buildProjectUsersRoute = (projectId: string): Route => ({
  route: `${ROUTES.ADMIN.PROJECT_USERS.route}?id=${projectId}`,
  url: `${ROUTES.ADMIN.PROJECT_USERS.url}?id=${projectId}`,
});

export const buildProjectOverlaysRoute = (projectId: string): Route => ({
  route: `${ROUTES.ADMIN.PROJECT_OVERLAYS.route}?id=${projectId}`,
  url: `${ROUTES.ADMIN.PROJECT_OVERLAYS.url}?id=${projectId}`,
});

export const buildProjectCommentsRoute = (projectId: string): Route => ({
  route: `${ROUTES.ADMIN.PROJECT_COMMENTS.route}?id=${projectId}`,
  url: `${ROUTES.ADMIN.PROJECT_COMMENTS.url}?id=${projectId}`,
});

export const buildProjectsWithNewProjectRoute = (projectId: string): Route => ({
  route: `${ROUTES.ADMIN.PROJECTS.route}?newProject=${projectId}`,
  url: `${ROUTES.ADMIN.PROJECTS.url}?newProject=${projectId}`,
});

export const buildEditUserRoute = (login: string): Route => ({
  route: `${ROUTES.ADMIN.EDIT_USER.route}?login=${login}`,
  url: `${ROUTES.ADMIN.EDIT_USER.url}?login=${login}`,
});

export const buildEditPrivilegesRoute = (login: string): Route => ({
  route: `${ROUTES.ADMIN.EDIT_PRIVILEGES.route}?login=${login}`,
  url: `${ROUTES.ADMIN.EDIT_PRIVILEGES.url}?login=${login}`,
});

export const buildEditPermalinkRoute = (id: number): Route => ({
  route: `${ROUTES.ADMIN.EDIT_PERMALINK.route}?id=${id}`,
  url: `${ROUTES.ADMIN.EDIT_PERMALINK.url}?id=${id}`,
});

export const buildEditPresetRoute = (id: string): Route => ({
  route: `${ROUTES.ADMIN.ANNOTATORS.PRESETS.EDIT.route}?id=${id}`,
  url: `${ROUTES.ADMIN.ANNOTATORS.PRESETS.EDIT.url}?id=${id}`,
});

export const buildBrowseProjectRoute = (projectId: string): Route => ({
  route: `${ROUTES.HOME.route}?id=${projectId}`,
  url: `${ROUTES.HOME.url}?id=${projectId}`,
});

export const buildBrowseProjectRouteFromQueryData = (queryData: QueryDataParams): Route => {
  let params = '';
  if (queryData.id) {
    params += `id=${queryData.id}`;
  } else {
    params += PROJECT_ID;
  }
  params += `&perfectMatch=${queryData.perfectMatch}`;
  if (queryData.z !== undefined) {
    params += `&z=${queryData.z}`;
  }
  if (queryData.x !== undefined) {
    params += `&x=${queryData.x}`;
  }
  if (queryData.y !== undefined) {
    params += `&y=${queryData.y}`;
  }
  if (queryData.modelId !== undefined) {
    params += `&modelId=${queryData.modelId}`;
  }
  if (queryData.backgroundId !== undefined) {
    params += `&backgroundId=${queryData.backgroundId}`;
  }
  if (queryData.overlaysId !== undefined) {
    params += `&overlaysId=${queryData.overlaysId}`;
  }
  if (queryData.pluginsId !== undefined) {
    params += `&pluginsId=${queryData.pluginsId}`;
  }
  if (queryData.searchValue !== undefined) {
    params += `&searchValue=${queryData.searchValue}`;
  }

  return {
    route: `${ROUTES.HOME.route}?${params}`,
    url: `${ROUTES.HOME.url}?${params}`,
  };
};
